import React, { useState } from 'react';
import {
  Box,
  SpaceBetween,
  Badge,
  TextContent,
} from '@cloudscape-design/components';
import {
  truncateText,
  getCitationAriaLabel,
} from '../utils/citationHelpers';
import './CitationPanel.css';

/**
 * CitationPanel - Renders citations as clickable cards with visual hierarchy.
 *
 * Requirements: 1.1, 1.5, 6.1, 6.4, 7.1, 7.2, 7.3, 7.4, 7.5, 8.1, 8.5
 */
function CitationPanel({ citations, onCitationClick }) {
  if (!citations || citations.length === 0) {
    return (
      <Box textAlign="center" color="text-body-secondary" padding="l">
        <TextContent>
          <p>No citations available.</p>
        </TextContent>
      </Box>
    );
  }

  return (
    <SpaceBetween size="m">
      {citations.map((citation, index) => (
        <CitationCard
          key={index}
          citation={citation}
          refNumber={index + 1}
          onCitationClick={onCitationClick}
        />
      ))}
    </SpaceBetween>
  );
}

function CitationCard({ citation, refNumber, onCitationClick }) {
  const [expanded, setExpanded] = useState(false);

  const truncated = truncateText(citation.text);
  const isLongText = citation.text && citation.text.length > 150;
  const displayText = expanded ? citation.text : truncated;

  const handleClick = () => {
    onCitationClick(citation);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onCitationClick(citation);
    }
  };

  const handleToggle = (e) => {
    e.stopPropagation();
    setExpanded((prev) => !prev);
  };

  const handleToggleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      setExpanded((prev) => !prev);
    }
  };

  const isMarketData = citation.type === 'market_data';

  const ariaLabel = isMarketData
    ? 'Market data source: AMZN historical prices'
    : getCitationAriaLabel(citation.source, citation.page);

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      style={isMarketData ? { ...styles.card, ...styles.marketCard } : styles.card}
      className="citation-card"
    >
      <SpaceBetween size="xs">
        {/* Source document name with ref number */}
        <Box fontWeight="bold" fontSize="body-m">
          <span style={styles.refBadge}>[{refNumber}]</span>{' '}
          {isMarketData ? '📈 ' : ''}{citation.source}
        </Box>

        {/* Page badge — or Market Data badge */}
        <div style={styles.badgeRow}>
          {isMarketData ? (
            <>
              <Badge color="green">Market Data</Badge>
              <Badge color="grey">{citation.page}</Badge>
            </>
          ) : (
            <Badge color="blue">{citation.page}</Badge>
          )}
        </div>

        {/* Truncated quote with show more/less toggle */}
        <TextContent>
          <p style={styles.quoteText}>
            &ldquo;{displayText}&rdquo;
            {isLongText && (
              <button
                type="button"
                onClick={handleToggle}
                onKeyDown={handleToggleKeyDown}
                style={styles.toggleButton}
                aria-label={expanded ? 'Show less text' : 'Show more text'}
              >
                {expanded ? 'show less' : 'show more'}
              </button>
            )}
          </p>
        </TextContent>
      </SpaceBetween>
    </div>
  );
}

const styles = {
  card: {
    padding: '12px 16px',
    borderRadius: '8px',
    border: '1px solid #e9ebed',
    backgroundColor: '#ffffff',
    cursor: 'pointer',
    transition: 'background-color 0.15s ease, box-shadow 0.15s ease',
    userSelect: 'none',
    WebkitTapHighlightColor: 'transparent',
  },
  marketCard: {
    border: '1px solid #16a085',
    backgroundColor: '#f0fdf9',
  },
  refBadge: {
    display: 'inline-block',
    color: '#0073bb',
    fontWeight: 700,
    fontSize: '12px',
    marginRight: '4px',
  },
  badgeRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
  },
  quoteText: {
    margin: 0,
    fontSize: '14px',
    color: '#545b64',
    lineHeight: '1.5',
  },
  toggleButton: {
    background: 'none',
    border: 'none',
    color: '#0972d3',
    cursor: 'pointer',
    fontSize: '14px',
    padding: '0 4px',
    textDecoration: 'underline',
    marginLeft: '4px',
  },
};

export default CitationPanel;
